import type { BlockPatternId, LessonThemeId } from '@teach/ui'
// src/features/editor/editorSlice.ts
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { ImageSlot } from '@/features/ai/images/lib/targetKeys'
import type { Lang } from '@/types/lang'
import { applyRemap, type IdAliases, type IdPair } from './lib/idAliases'

export { resolveAlias } from './lib/idAliases'

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'
export type ContentLang = Lang

type PanelKey = 'aiSidebarOpen' | 'blockPanelOpen'
type DialogKey = 'settingsOpen' | 'publishOpen' | 'drawImagesOpen' | 'previewOpen'

interface EditorState {
  selectedLessonId: number | null
  selectedBlockId: number | null
  /**
   * Мультивыбор блоков для правки промптом (п.10 бэк-лога) — Ctrl/Cmd-клик по
   * миниатюре. `selectedBlockId` остаётся якорем панели/превью/черновика и
   * этот набор НЕ заменяет. Читателей ДВА, и роли у них разные: `useEditScope`
   * — единственный, кто строит по набору ОБЛАСТЬ ПРАВКИ (`'selection'`), а
   * `useCourseTimeline` только пробрасывает отметку в миниатюру (`multiSelected`)
   * — отмеченность обязана быть видна, иначе набор невидим автору. Порядок
   * кликов тут не важен: `useEditScope` отдаёт id в порядке `order` блоков.
   */
  selectedBlockIds: number[]
  contentLang: ContentLang
  /**
   * Язык, которым слот засеян при открытии курса, — исходный язык курса
   * (`seedLangFor`). Авто-режим отрисовки рисует ТОЛЬКО этот слот (§5.3),
   * поэтому места промпта обязаны предупредить автора, когда он правит второй.
   * `null` до засева: подпись не должна мелькать на первом кадре.
   */
  seededLang: ContentLang | null
  saveStatus: SaveStatus
  panels: Record<PanelKey, boolean>
  /** Уже `lg` (телефон, планшет): выехала ли панель экрана/урока поверх холста.
   *  В сторе, а не в стейте рабочей области: открыть её может и плашка урока в ленте. */
  blockSheetOpen: boolean
  /** Открыта вкладка «Оформление» панели урока: выбранное оформление для экрана на холсте. */
  lessonLookPreview: { theme: LessonThemeId | null; pattern: BlockPatternId | null } | null
  dialogs: Record<DialogKey, boolean>
  /**
   * Слот, которым открыт диалог отрисовки: кнопка в шапке редактора открывает
   * его базовым (`null`), кнопка из итога перевода — вторым языком. Живёт
   * рядом с флагом диалога, потому что это ПАРАМЕТР ОДНОГО открытия, а не
   * настройка автора: `setDialogOpen` гасит его вместе с диалогом.
   */
  drawImagesSlot: ImageSlot | null
  /**
   * Вкладка, на которой открывается окно настроек курса, когда его зовут ради
   * конкретного поля (ссылка «Задать стиль картинок» ведёт во вкладку «Дизайн-система»).
   * Параметр ОДНОГО открытия, как `drawImagesSlot`: гасится вместе с окном; `null` —
   * первая вкладка.
   */
  settingsTab?: string | null
  /**
   * Язык полей «Основного», с которым открыть настройки («Перейти» у незаполненного
   * поля EN). Параметр ОДНОГО открытия, как `settingsTab`; `null` — язык правки.
   */
  settingsLang?: ContentLang | null
  /**
   * Счётчик запросов языка: растёт на каждом `setSettingsLang` с непустым языком.
   * Без него повторный «Перейти» на тот же язык не меняет `settingsLang`, и эффект
   * полей, уже переключённых вручную, не срабатывает.
   */
  settingsLangSeq: number
  /**
   * Зонд первого черновика: id живого курса без черновика, открытого в редакторе.
   * Первая правка такого курса заводит черновик на бэке, и узнать об этом можно
   * только перечитав `/moderation` — это делает `draftProbeMiddleware` после каждой
   * успешной пишущей мутации, пока зонд стоит. Ставит и снимает `ModerationPoller`.
   */
  draftProbeCourseId: number | null
  /**
   * Признак оседания источника: id курса, у которого зонд перечитывает `/moderation`
   * после записи или уже нашёл черновик, а редактор ещё не переехал на него. Пока он
   * стоит, пакетные записи (реордер, оформление урока, фоновый коммит загрузки) не
   * уходят — их тела собраны из живой версии и затёрли бы черновик. Ставит
   * `draftProbeMiddleware`, снимают он же (ошибка/нет черновика/сторож),
   * `useReadSourceRemap` (переезд закончен) и `ModerationPoller` (размонтирование).
   * Читать через `moderation/lib/draftSourceSettling.ts`.
   */
  draftSourceSettling: number | null
  /**
   * Старый id урока/блока → новый после смены источника чтения (`'lesson:10'` → 50,
   * `'block:11'` → 51). Читать через `resolveAlias`: цепочки разворачивает он.
   */
  idAliases: IdAliases
}

const initialState: EditorState = {
  selectedLessonId: null,
  selectedBlockId: null,
  selectedBlockIds: [],
  contentLang: 'ru',
  seededLang: null,
  saveStatus: 'idle',
  // Ref parity: AI sidebar starts collapsed (only the diamond logo toggle shows).
  panels: { aiSidebarOpen: false, blockPanelOpen: true },
  blockSheetOpen: false,
  lessonLookPreview: null,
  dialogs: {
    settingsOpen: false,
    publishOpen: false,
    drawImagesOpen: false,
    previewOpen: false,
  },
  drawImagesSlot: null,
  settingsTab: null,
  settingsLang: null,
  settingsLangSeq: 0,
  draftProbeCourseId: null,
  draftSourceSettling: null,
  idAliases: {},
}

export const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    selectLesson: (state, action: PayloadAction<number | null>) => {
      // Набор мультивыбора чистит только СМЕНА урока: прежний набор про блоки
      // прежнего урока, показывать «отмеченным» экран, который к правке уже не
      // относится, нельзя. Сравнение обязано идти ДО присваивания — обычный
      // клик по миниатюре шлёт `selectLesson` того же урока (`selectBlockInLesson`),
      // и безусловная чистка молча обнуляла набранную область правки.
      if (state.selectedLessonId !== action.payload) state.selectedBlockIds = []
      state.selectedLessonId = action.payload
      state.selectedBlockId = null
    },
    selectBlock: (state, action: PayloadAction<number | null>) => {
      state.selectedBlockId = action.payload
    },
    /** Ctrl/Cmd-клик по миниатюре — добавляет/убирает id из набора мультивыбора. */
    toggleBlockSelection: (state, action: PayloadAction<number>) => {
      const id = action.payload
      const idx = state.selectedBlockIds.indexOf(id)
      if (idx === -1) state.selectedBlockIds.push(id)
      else state.selectedBlockIds.splice(idx, 1)
    },
    setContentLang: (state, action: PayloadAction<ContentLang>) => {
      state.contentLang = action.payload
    },
    /** Засев на открытии курса: ставит слот правки И запоминает, что он
     *  исходный. Переключатель языка зовёт `setContentLang` — он `seededLang`
     *  не трогает, иначе признак «правится второй слот» не с чем было бы
     *  сравнивать. */
    seedContentLang: (state, action: PayloadAction<ContentLang>) => {
      state.contentLang = action.payload
      state.seededLang = action.payload
    },
    setSaveStatus: (state, action: PayloadAction<SaveStatus>) => {
      state.saveStatus = action.payload
    },
    setPanelOpen: (state, action: PayloadAction<{ panel: PanelKey; open: boolean }>) => {
      state.panels[action.payload.panel] = action.payload.open
    },
    setBlockSheetOpen: (state, action: PayloadAction<boolean>) => {
      state.blockSheetOpen = action.payload
    },
    setLessonLookPreview: (state, action: PayloadAction<EditorState['lessonLookPreview']>) => {
      state.lessonLookPreview = action.payload
    },
    setDialogOpen: (state, action: PayloadAction<{ dialog: DialogKey; open: boolean }>) => {
      const { dialog, open } = action.payload
      state.dialogs[dialog] = open
      // Слот принадлежит ОДНОМУ открытию: без сброса на закрытии следующее
      // открытие из шапки редактора застало бы второй язык прошлого раза.
      if (dialog === 'drawImagesOpen' && !open) state.drawImagesSlot = null
      if (dialog === 'settingsOpen' && !open) {
        state.settingsTab = null
        state.settingsLang = null
      }
    },
    /** Ставится ПЕРЕД `setDialogOpen` тем, кто открывает диалог отрисовки
     *  предметно (итог перевода) — второго механизма открытия не заводим. */
    /** Ставится ПЕРЕД `setDialogOpen` тем, кто открывает настройки предметно. */
    setSettingsTab: (state, action: PayloadAction<string | null>) => {
      state.settingsTab = action.payload
    },
    /** Ставится ПЕРЕД `setDialogOpen` — язык полей для этого открытия настроек. */
    setSettingsLang: (state, action: PayloadAction<ContentLang | null>) => {
      state.settingsLang = action.payload
      if (action.payload) state.settingsLangSeq += 1
    },
    setDrawImagesSlot: (state, action: PayloadAction<ImageSlot | null>) => {
      state.drawImagesSlot = action.payload
    },
    /** Смена источника чтения: выделение и алиасы переводит `applyRemap`. */
    remapSelection: (state, action: PayloadAction<{ lessons: IdPair[]; blocks: IdPair[] }>) => {
      applyRemap(state, action.payload)
    },
    setDraftProbe: (state, action: PayloadAction<number | null>) => {
      state.draftProbeCourseId = action.payload
    },
    setDraftSourceSettling: (state, action: PayloadAction<number | null>) => {
      state.draftSourceSettling = action.payload
    },
    /** Снять признак оседания, только если он стоит на ЭТОМ курсе. */
    draftSourceSettled: (state, action: PayloadAction<number>) => {
      if (state.draftSourceSettling === action.payload) state.draftSourceSettling = null
    },
    // Зонд и признак оседания принадлежат `ModerationPoller` и зонду (поллер смонтирован
    // выше рабочей области и сам снимает их на размонтировании), поэтому сброс рабочей
    // области их не трогает.
    resetEditor: (state) => ({
      ...initialState,
      draftProbeCourseId: state.draftProbeCourseId,
      draftSourceSettling: state.draftSourceSettling,
    }),
  },
})

export const {
  selectLesson,
  selectBlock,
  toggleBlockSelection,
  setContentLang,
  seedContentLang,
  setSaveStatus,
  setPanelOpen,
  setBlockSheetOpen,
  setLessonLookPreview,
  setDialogOpen,
  setDrawImagesSlot,
  setSettingsTab,
  setSettingsLang,
  setDraftProbe,
  setDraftSourceSettling,
  draftSourceSettled,
  remapSelection,
  resetEditor,
} = editorSlice.actions
export const editorReducer = editorSlice.reducer
